import React, { useCallback } from 'react';
import { Match } from 'effect';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { createFirstLayerProxyTypeValue, getExtrapolateFromValue, getProxyTypeLabel } from 'venn-utils';
import { TabState, LayeredProxyConfiguration } from '../proxy-types';
import type { ProxyTypeEnum } from 'venn-api';
import type { LayeredDetailedProxyMetadata } from '../../../../hooks/useProxyDataQuery';
import type { SelectedProxy, ReturnsRangeInfo } from '../utils';
import type { FundToBulkProxy } from '../../types';
import type { BulkProxyValidationState } from '../useProxyErrorMessage';
import { BulkProxyLegend } from '../BulkProxyLegend';
import { BulkCategoryOptions } from '../BulkCategoryOptions';
import { BulkProxySearch } from './BulkProxySearch';
import { ProxySummaryTable } from '../ProxySummaryTable';
import styled from 'styled-components';
import { ProxyPickerAction, type ProxyTabValue } from '../hooks/types';
import { AlertNote, Icon } from 'venn-ui-kit';

type ProxyConfigurationTabsAction =
  | ReturnType<typeof ProxyPickerAction.ChangeTab>
  | ReturnType<typeof ProxyPickerAction.SelectProxy>
  | ReturnType<typeof ProxyPickerAction.SetNumLags>;

interface ProxyConfigurationTabsProps {
  configuration: LayeredProxyConfiguration;
  hasSingleInvestment: boolean;
  supportsCategoryPicker: boolean;
  selectedProxyType: ProxyTypeEnum | undefined;
  selectedProxy: SelectedProxy | null;
  rawInvestmentReturnsRanges: (ReturnsRangeInfo | null)[] | undefined;
  effectiveSecondLayerRanges: (ReturnsRangeInfo | null)[] | undefined;
  investments: FundToBulkProxy[];
  shouldExtrapolate: boolean;
  numLags: number | undefined;
  suggestedNumLags: number | undefined;
  validationState: BulkProxyValidationState;
  returnsProxyDataByFund: Record<string, LayeredDetailedProxyMetadata>;
  activeTab: ProxyTabValue;
  performAction: (action: ProxyConfigurationTabsAction) => void;
  showRequireUpdatingOtherProxy?: boolean;
}

const SearchLabel = styled.div`
  font-weight: bold;
  font-size: 14px;
`;

export const ProxyConfigurationTabs = ({
  configuration,
  hasSingleInvestment,
  supportsCategoryPicker,
  selectedProxyType,
  selectedProxy,
  rawInvestmentReturnsRanges,
  effectiveSecondLayerRanges,
  investments,
  shouldExtrapolate,
  numLags,
  suggestedNumLags,
  validationState,
  returnsProxyDataByFund,
  activeTab,
  performAction,
  showRequireUpdatingOtherProxy,
}: ProxyConfigurationTabsProps) => {
  const filterValidRanges = (ranges: (ReturnsRangeInfo | null)[] | undefined) =>
    ranges?.filter(
      (
        r,
      ): r is ReturnsRangeInfo & {
        start: number;
        end: number;
        frequency: NonNullable<ReturnsRangeInfo['frequency']>;
      } => r !== null && r.start !== undefined && r.end !== undefined && r.frequency !== undefined,
    ) ?? [];

  const validReturnsRanges = filterValidRanges(rawInvestmentReturnsRanges);
  const effectiveSecondLayerValidRanges = filterValidRanges(effectiveSecondLayerRanges);

  const tabs = Match.value(configuration).pipe(
    Match.tag('Empty', () => [TabState.Empty({})]),
    Match.tag('SingleLayerable', ({ proxy }) => [
      TabState.WithProxy({
        proxyTypeValue: createFirstLayerProxyTypeValue(proxy.proxyType, getExtrapolateFromValue(proxy)),
        selectedProxy: proxy.selectedProxy,
      }),
    ]),
    Match.tag('SingleNonLayerable', ({ proxy }) => [
      TabState.WithProxy({
        proxyTypeValue: createFirstLayerProxyTypeValue(proxy.proxyType, getExtrapolateFromValue(proxy)),
        selectedProxy: proxy.selectedProxy,
      }),
    ]),
    Match.tag('Multi', ({ firstLayer, secondLayer }) => [
      TabState.WithProxy({
        proxyTypeValue: createFirstLayerProxyTypeValue(firstLayer.proxyType, getExtrapolateFromValue(firstLayer)),
        selectedProxy: firstLayer.selectedProxy,
      }),
      TabState.WithProxy({
        proxyTypeValue: createFirstLayerProxyTypeValue(secondLayer.proxyType, getExtrapolateFromValue(secondLayer)),
        selectedProxy: secondLayer.selectedProxy,
      }),
    ]),
    Match.exhaustive,
  );

  const renderTabLabel = (tab: TabState, index: number) => {
    return Match.value(tab).pipe(
      Match.tag('Empty', () => `${index + 1}: PROXY TYPE`),
      Match.tag(
        'WithProxy',
        ({ proxyTypeValue }) => `${index + 1}: ${getProxyTypeLabel(proxyTypeValue).toUpperCase()}`,
      ),
      Match.exhaustive,
    );
  };

  const isTabEmpty = TabState.$is('Empty');

  const tabValue = activeTab === 'first' ? 'tab-1' : 'tab-2';

  const handleTabChange = useCallback(
    (value: string) => {
      performAction(ProxyPickerAction.ChangeTab({ tab: value === 'tab-1' ? 'first' : 'second' }));
    },
    [performAction],
  );

  const handleSelectCategory = useCallback(
    (proxy: SelectedProxy | null, lags: number | undefined) => {
      performAction(ProxyPickerAction.SelectProxy({ proxy }));
      if (lags !== undefined) {
        performAction(ProxyPickerAction.SetNumLags({ lags }));
      }
    },
    [performAction],
  );

  const handleSelectProxy = useCallback(
    (proxy: SelectedProxy | null) => {
      performAction(ProxyPickerAction.SelectProxy({ proxy }));
    },
    [performAction],
  );

  return (
    <Tabs value={tabValue} onValueChange={handleTabChange} className="flex flex-col flex-1">
      <TabsList className="h-auto p-0 !pl-[24px] bg-transparent gap-0 rounded-none justify-start">
        {tabs.map((tab, index) => (
          <TabsTrigger
            key={`tab-${index + 1}`}
            value={`tab-${index + 1}`}
            className={cn(
              'bg-venn-grey-10 border border-solid border-venn-grey-30 rounded-t px-[12px] text-[11px]',
              'data-[state=active]:border-b-0 data-[state=active]:font-bold data-[state=active]:bg-venn-grey-10',
              'data-[state=inactive]:border-b data-[state=inactive]:font-bold data-[state=inactive]:bg-white data-[state=inactive]:hover:bg-venn-switch-light',
              isTabEmpty(tab) && '[&&]:text-venn-grey-60',
              !isTabEmpty(tab) && 'data-[state=active]:text-venn-black',
              !isTabEmpty(tab) && 'data-[state=inactive]:text-venn-dark-blue',
              'first:rounded-tl first:rounded-bl-none',
              'last:rounded-tr last:rounded-br-none',
              index > 0 && '-ml-px',
              'py-[8px]',
            )}
          >
            <span>
              {tab.selectedProxy && <Icon type="check" className="text-venn-green mr-[3px]" />}
              {renderTabLabel(tab, index)}
            </span>
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((tab, index) => (
        <TabsContent
          key={`tab-${index + 1}`}
          value={`tab-${index + 1}`}
          className="mt-0 flex flex-1 flex-col border border-t-0 border-solid border-venn-grey-30 bg-venn-grey-10 min-h-[200px]"
        >
          <div className="p-[24px] pb-0">
            {supportsCategoryPicker ? (
              <>
                <div className="flex items-center justify-between mb-[8px] px-[12px]">
                  <SearchLabel className="pr-[6px]">Select a proxy</SearchLabel>
                  {hasSingleInvestment ? <BulkProxyLegend /> : null}
                </div>
                <BulkCategoryOptions
                  investments={investments}
                  proxyType={selectedProxyType}
                  selectedProxy={selectedProxy}
                  onSelect={handleSelectCategory}
                  rawInvestmentReturnsRanges={
                    index === 1 && effectiveSecondLayerValidRanges.length > 0
                      ? effectiveSecondLayerValidRanges
                      : validReturnsRanges
                  }
                  selectedProxyType={selectedProxyType}
                  numLags={numLags || suggestedNumLags}
                  extrapolate={shouldExtrapolate}
                  firstLayerProxyMeta={
                    index === 1 && LayeredProxyConfiguration.$is('Multi')(configuration)
                      ? {
                          proxyId: configuration.firstLayer.selectedProxy?.id ?? '',
                          proxyType: configuration.firstLayer.proxyType,
                          numLags: configuration.firstLayer.numLags,
                          extrapolate: getExtrapolateFromValue(configuration.firstLayer),
                        }
                      : undefined
                  }
                />
              </>
            ) : (
              <>
                <div className="flex flex-row mb-[8px] w-full justify-between items-end">
                  <SearchLabel>Select an investment to use as a proxy</SearchLabel>
                  {showRequireUpdatingOtherProxy && index === 0 && (
                    <AlertNote size="small">
                      Changing the backfill proxy may require updating the other proxy.
                    </AlertNote>
                  )}
                </div>
                <BulkProxySearch
                  selectedProxy={selectedProxy}
                  setSelectedProxy={handleSelectProxy}
                  rawInvestmentReturnsRanges={
                    index === 1 && effectiveSecondLayerValidRanges.length > 0
                      ? effectiveSecondLayerValidRanges
                      : validReturnsRanges
                  }
                  selectedProxyType={selectedProxyType}
                  investments={investments}
                  shouldExtrapolate={shouldExtrapolate}
                  autoProxyEnabled
                />
              </>
            )}
          </div>
          <div className="mt-auto">
            <ProxySummaryTable
              proxyType={selectedProxyType}
              investments={investments}
              validationState={validationState}
              proxyDataByFund={returnsProxyDataByFund}
            />
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
};
